/**
 * React hooks for Flux Agent speech: push-to-talk dictation that fills the composer, and the
 * hands-free conversation (continuous listening, streamed replies spoken sentence by sentence,
 * barge-in). Dictation records with `MediaRecorder` (WebM/Opus, or MP4/AAC on Safari) and
 * uploads the raw bytes; voice mode runs {@link VoiceEngine}. Both release the microphone,
 * audio context and pending playback on stop or unmount.
 *
 * @module apps/web/src/components/flux-agent/useFluxAgentVoice
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fluxAgentApi } from '@/lib/fluxAgentApi';
import {
  MicrophoneError,
  closeMicrophone,
  loadVoiceSensitivity,
  openMicrophone,
  saveVoiceSensitivity,
  startRecording,
  type Recording,
  type RecordingHandle,
  type VoiceSensitivity,
  type VoiceTurnTimings,
} from '@/lib/fluxAgentVoice';
import { VoiceEngine, type VoiceEngineState } from '@/lib/fluxAgentVoiceEngine';
import { formatBytes } from '@/lib/fluxAgentFiles';

/** Longest dictation clip before recording stops on its own. */
const DICTATION_MAX_MS = 120_000;
/** Turn timings kept for the debug overlay. */
const TIMING_HISTORY = 5;

/** User-facing text for a microphone failure; non-{@link MicrophoneError} causes get generic copy. */
function microphoneMessage(error: unknown): string {
  return error instanceof MicrophoneError ? error.message : 'The microphone could not be used.';
}

/**
 * Upload a recording's raw bytes to `/speech/transcribe` and return the trimmed text (possibly
 * empty). Throws with user-facing copy when the blob exceeds `maxAudioBytes` (0 or less skips
 * the check) or the server call fails.
 */
async function transcribeRecording(recording: Recording, maxAudioBytes: number): Promise<string> {
  if (maxAudioBytes > 0 && recording.blob.size > maxAudioBytes) {
    throw new Error(
      `That recording is over the ${formatBytes(maxAudioBytes)} limit. Try a shorter one.`
    );
  }
  const result = await fluxAgentApi.transcribeAudio(recording.blob, recording.mimeType);
  if (!result.ok) throw new Error(result.message);
  return result.data.text.trim();
}

/** Dictation lifecycle exposed to the composer. */
export interface FluxAgentDictation {
  /** `recording` while capturing, `transcribing` while awaiting the server, else `idle`. */
  state: 'idle' | 'recording' | 'transcribing';
  /** Smoothed 0–1 microphone level while recording. */
  level: number;
  /** Last microphone, size-limit, transcription or no-speech message; null when clear. */
  error: string | null;
  /**
   * Open the microphone and record until `stop()`, two minutes, or the byte limit, then transcribe
   * and pass non-empty text to `onText`. Ignored while a recording is already running.
   */
  start: () => Promise<void>;
  /** Stop recording and transcribe what was captured. */
  stop: () => void;
  /** Dismiss the current error message. */
  clearError: () => void;
}

/**
 * Record a clip on demand and pass the transcription to `onText`. Recording stops automatically
 * at two minutes or just before the byte limit.
 */
export function useFluxAgentDictation(
  maxAudioBytes: number,
  onText: (text: string) => void
): FluxAgentDictation {
  const [state, setState] = useState<FluxAgentDictation['state']>('idle');
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const handle = useRef<RecordingHandle | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const mounted = useRef(true);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  useEffect(() => {
    // Set again on every mount: a keep-alive reveal remounts effects without a new ref.
    mounted.current = true;
    return () => {
      mounted.current = false;
      handle.current?.stop();
      closeMicrophone(stream.current);
    };
  }, []);

  const start = useCallback(async () => {
    if (handle.current) return;
    setError(null);
    try {
      stream.current = await openMicrophone();
    } catch (cause) {
      setError(microphoneMessage(cause));
      return;
    }
    if (!mounted.current) return closeMicrophone(stream.current);
    const recording = startRecording(stream.current, {
      maxBytes: maxAudioBytes || 10 * 1024 * 1024,
      maxMs: DICTATION_MAX_MS,
      onLevel: setLevel,
    });
    handle.current = recording;
    setState('recording');
    try {
      const clip = await recording.done;
      closeMicrophone(stream.current);
      if (!mounted.current) return;
      setState('transcribing');
      setLevel(0);
      const text = await transcribeRecording(clip, maxAudioBytes);
      if (!mounted.current) return;
      if (text) onTextRef.current(text);
      else setError('No speech was recognised. Try again a little closer to the microphone.');
    } catch (cause) {
      if (mounted.current)
        setError(cause instanceof Error ? cause.message : 'Transcription failed.');
    } finally {
      closeMicrophone(stream.current);
      stream.current = null;
      handle.current = null;
      if (mounted.current) {
        setState('idle');
        setLevel(0);
      }
    }
  }, [maxAudioBytes]);

  const stop = useCallback(() => handle.current?.stop(), []);
  const clearError = useCallback(() => setError(null), []);

  return { state, level, error, start, stop, clearError };
}

/** Visible phase of hands-free voice mode (the engine state). */
export type VoicePhase = VoiceEngineState;

/** Options for {@link useFluxAgentVoiceMode}. */
export interface VoiceModeOptions {
  /** Server transcription limit in bytes; utterances stop before exceeding it (0 = 45 s cap only). */
  maxAudioBytes: number;
  /** Whether the server synthesizes speech; otherwise the browser voice speaks each sentence. */
  serverSpeech: boolean;
  /**
   * Send the transcript as a chat message, passing streamed answer text to `onDelta` and the
   * utterance's prosody numbers (when measured) with the message. Resolve with the stored reply
   * text, or `null` when sending failed (voice mode keeps listening and the page shows the error).
   */
  send: (
    text: string,
    onDelta: (text: string, round: number) => void,
    prosody?: unknown
  ) => Promise<string | null>;
  /** Expose `window.__fluxAgentVoice` for tests and the debug overlay (`?voiceDebug=1`). */
  debug?: boolean;
}

/** Voice-mode lifecycle exposed to the page. */
export interface FluxAgentVoiceMode {
  /** Current step; `off` when voice mode is not running. */
  phase: VoicePhase;
  /** Smoothed 0–1 microphone level while the microphone is open; 0 otherwise. */
  level: number;
  /** Why voice mode stopped, or a recoverable problem (transcription failed); null when clear. */
  error: string | null;
  /** Start voice mode. Call directly from a click or tap: iOS only unlocks audio in the gesture. */
  start: () => void;
  /** Resume after the page was hidden; also needs a tap. */
  resume: () => void;
  /** Stop voice mode and release the microphone. */
  stop: () => void;
  /** While `hearing`, end the utterance now instead of waiting for silence. */
  finishUtterance: () => void;
  /** While `thinking` or `speaking`, stop the agent and listen again. */
  interrupt: () => void;
  /** Detector sensitivity, persisted in localStorage. */
  sensitivity: VoiceSensitivity;
  /** Change and persist the sensitivity. */
  setSensitivity: (value: VoiceSensitivity) => void;
  /** Latency of recent turns, newest first. */
  timings: VoiceTurnTimings[];
  /** True while reply audio is playing or scheduled (for the debug overlay and tests). */
  playing: boolean;
}

/** Debug handle installed on `window.__fluxAgentVoice` when `debug` is set. */
export interface FluxAgentVoiceDebugHandle {
  /** Override the measured microphone RMS (0-1) until called with `null`. */
  injectLevel(level: number | null): void;
  /** Current engine state. */
  readonly state: VoicePhase;
  /** Whether reply audio is playing. */
  readonly playing: boolean;
}

/**
 * Run hands-free voice mode. The microphone stays open; the engine detects each utterance,
 * transcribes it, streams the reply, speaks it sentence by sentence as it arrives, and listens
 * again. Speaking while the agent thinks or talks interrupts it.
 */
export function useFluxAgentVoiceMode({
  maxAudioBytes,
  serverSpeech,
  send,
  debug = false,
}: VoiceModeOptions): FluxAgentVoiceMode {
  const [phase, setPhase] = useState<VoicePhase>('off');
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [sensitivity, setSensitivityState] = useState<VoiceSensitivity>(() =>
    loadVoiceSensitivity()
  );
  const [timings, setTimings] = useState<VoiceTurnTimings[]>([]);
  const [playing, setPlaying] = useState(false);
  const engine = useRef<VoiceEngine | null>(null);
  const lastLevel = useRef(0);
  const sendRef = useRef(send);
  sendRef.current = send;
  const serverSpeechRef = useRef(serverSpeech);
  serverSpeechRef.current = serverSpeech;
  const sensitivityRef = useRef(sensitivity);
  sensitivityRef.current = sensitivity;

  const stop = useCallback(() => {
    engine.current?.stop();
    engine.current = null;
    setPhase('off');
    setLevel(0);
    setPlaying(false);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback(() => {
    if (engine.current) return;
    setError(null);
    const created = new VoiceEngine(
      {
        transcribe: async audio => {
          const result = await fluxAgentApi.transcribeAudio(audio, 'audio/wav');
          if (!result.ok) throw new Error(result.message);
          return result.data.text.trim();
        },
        synthesize: async (text, signal) => {
          if (!serverSpeechRef.current) return null;
          const result = await fluxAgentApi.synthesizeAudio(text, signal);
          return result.ok ? result.data : null;
        },
        send: (text, onDelta, prosody) => sendRef.current(text, onDelta, prosody),
      },
      {
        onState: state => {
          if (engine.current !== created) return;
          setPhase(state);
          setPlaying(created.playing);
          if (state === 'off') engine.current = null;
        },
        onLevel: value => {
          if (engine.current !== created) return;
          // Skip imperceptible meter changes so the page does not re-render on every frame.
          if (
            Math.abs(value - lastLevel.current) >= 0.03 ||
            (value === 0) !== (lastLevel.current === 0)
          ) {
            lastLevel.current = value;
            setLevel(value);
          }
          setPlaying(created.playing);
        },
        onError: setError,
        onTimings: value => setTimings(current => [value, ...current].slice(0, TIMING_HISTORY)),
      },
      { sensitivity: sensitivityRef.current, maxAudioBytes }
    );
    engine.current = created;
    // Synchronous up to its first await, so the AudioContext is created inside the gesture.
    void created.start();
  }, [maxAudioBytes]);

  const resume = useCallback(() => void engine.current?.resume(), []);
  const finishUtterance = useCallback(() => engine.current?.finishUtterance(), []);
  const interrupt = useCallback(() => engine.current?.interrupt(), []);
  const setSensitivity = useCallback((value: VoiceSensitivity) => {
    setSensitivityState(value);
    saveVoiceSensitivity(value);
    engine.current?.setSensitivity(value);
  }, []);

  useEffect(() => {
    if (!debug) return;
    const handle: FluxAgentVoiceDebugHandle = {
      injectLevel: value => engine.current?.injectLevel(value),
      get state() {
        return engine.current?.state ?? 'off';
      },
      get playing() {
        return engine.current?.playing ?? false;
      },
    };
    (window as unknown as { __fluxAgentVoice?: FluxAgentVoiceDebugHandle }).__fluxAgentVoice =
      handle;
    return () => {
      delete (window as unknown as { __fluxAgentVoice?: FluxAgentVoiceDebugHandle })
        .__fluxAgentVoice;
    };
  }, [debug]);

  return {
    phase,
    level,
    error,
    start,
    resume,
    stop,
    finishUtterance,
    interrupt,
    sensitivity,
    setSensitivity,
    timings,
    playing,
  };
}
